import { onBeforeUnmount, ref, Ref, watch } from "@vue/composition-api";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { EVENTS } from "@/const";

export type FollowingOptions = { watchMode?: boolean; globalCapture?: boolean };

export type FollowingUpdateData = { userId: string; followingStatus: boolean };

interface UseFollowingResponse {
  loading: Ref<boolean>;
  followingIdsList: Ref<string[]>;
  toggle: (userId: string) => Promise<boolean>;
  checkFollowingById: (userId: string, buffered?: boolean) => Promise<boolean>;
  checkAllFollowingsById: (userIds: string[], buffered?: boolean) => Promise<string[]>;
}

export default function useFollowing(list: Ref<string[]>, options?: FollowingOptions): UseFollowingResponse {
  let calledIdsBuffer: Set<string> = new Set();
  const followingIdsList = ref<string[]>([]);
  const loading = ref<boolean>(false);
  const { authUser } = useAuthUser();

  async function searchFollowings(ids: string[]): Promise<string[]> {
    if (ids.length <= 0) return followingIdsList.value;
    ids.forEach(id => calledIdsBuffer.add(id));
    const response = await providers.FollowingsProvider.searchFollowings({ ids });
    return (followingIdsList.value = followingIdsList.value.concat(response.map(i => i.userId)));
  }

  const checkFollowingById = async (userId: string, buffered = true): Promise<boolean> => {
    if (buffered && followingIdsList.value.find(i => i === userId)) return true;
    const response = await searchFollowings([userId]);
    return response.includes(userId);
  };

  const checkAllFollowingsById = async (userIds: string[], buffered = true): Promise<string[]> => {
    if (buffered && userIds.every(id => followingIdsList.value.find(i => i === id))) return userIds;
    const idsToLoad = userIds.filter(id => !(buffered ? [...calledIdsBuffer.values()] : []).find(i => i === id));
    return await searchFollowings(idsToLoad);
  };

  const updateListBySingleId = (dataToUpdate?: FollowingUpdateData): void => {
    if (!dataToUpdate) return;
    if (dataToUpdate.followingStatus) {
      calledIdsBuffer.add(dataToUpdate.userId);
      followingIdsList.value.push(dataToUpdate.userId);
    } else {
      calledIdsBuffer.delete(dataToUpdate.userId);
      followingIdsList.value = followingIdsList.value.filter(i => i !== dataToUpdate.userId);
    }
  };
  const toggle = async (userId: string): Promise<boolean> => {
    const response = await providers.FollowingsProvider.toggle({ id: userId });
    const data: FollowingUpdateData = { userId, followingStatus: response };
    emitter.emit(EVENTS.FOLLOWING_STATUS_CHANGE, data);
    if (!options?.globalCapture) {
      updateListBySingleId(data);
    }
    return response;
  };

  if (options?.globalCapture) {
    emitter.on<FollowingUpdateData>(EVENTS.FOLLOWING_STATUS_CHANGE, updateListBySingleId);
    emitter.on<FollowingUpdateData>(EVENTS.SUBSCRIPTION_STATUS_CHANGED, updateListBySingleId);
  }
  onBeforeUnmount(() => {
    if (options?.globalCapture) {
      emitter.off<FollowingUpdateData>(EVENTS.FOLLOWING_STATUS_CHANGE, updateListBySingleId);
      emitter.off<FollowingUpdateData>(EVENTS.SUBSCRIPTION_STATUS_CHANGED, updateListBySingleId);
    }
  });

  const _checkAllFollowingsById = async () => {
    if (!authUser.value || list.value.length === 0) return;
    loading.value = true;
    try {
      await checkAllFollowingsById(list.value);
    } finally {
      loading.value = false;
    }
  };

  if (options?.watchMode) {
    watch(list, _checkAllFollowingsById, { immediate: options?.watchMode });

    watch(authUser, async (newAuthUser, oldAuthUser) => {
      if (newAuthUser?.id !== oldAuthUser?.id) {
        calledIdsBuffer.clear();
        followingIdsList.value = [];
        await _checkAllFollowingsById();
      }
    });
  }

  return {
    loading,
    followingIdsList,
    toggle,
    checkFollowingById,
    checkAllFollowingsById,
  };
}
